import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  SECTORS, RECURRENCE_LEVELS, EDUCATION_LEVELS,
  BACKGROUNDS, FUND_MODELS, SEARCH_STAGES, CONTINUITY_PREFS,
} from "../../lib/taxonomy";
import GeographySelect from "../../components/GeographySelect";
import { ArrowRight, ArrowLeft, Briefcase, Rocket, Target, Sparkles, Lock, Shield } from "lucide-react";
import { toast } from "sonner";
import { t } from "../../lib/i18n";

const STEPS = [
  { icon: Briefcase, label: t("Tu trayectoria"), kicker: t("Paso 1 · Quién eres") },
  { icon: Rocket, label: t("Tu search fund"), kicker: t("Paso 2 · Tu vehículo") },
  { icon: Target, label: t("Tu empresa ideal"), kicker: t("Paso 3 · Qué buscas") },
  { icon: Sparkles, label: t("Tu motivación"), kicker: t("Paso 4 · Por qué tú") },
];

export default function SearcherOnboarding() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const existing = user?.searcher_profile || {};
  const isEdit = Boolean(user?.searcher_profile_completed);

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    current_position: existing.current_position || "",
    years_experience: existing.years_experience ?? "",
    education: existing.education || "",
    background: existing.background || [],
    linkedin_url: existing.linkedin_url || "",
    fund_name: existing.fund_name || "",
    fund_model: existing.fund_model || "",
    search_stage: existing.search_stage || "",
    committed_capital_eur: existing.committed_capital_eur ?? "",
    backers_count: existing.backers_count ?? "",
    dedication: existing.dedication || "",
    sectors_interest: existing.sectors_interest || [],
    geographies: existing.geographies || [],
    ebitda_min_eur: existing.ebitda_min_eur ?? "",
    ebitda_max_eur: existing.ebitda_max_eur ?? "",
    revenue_min_eur: existing.revenue_min_eur ?? "",
    revenue_max_eur: existing.revenue_max_eur ?? "",
    min_recurrence: existing.min_recurrence || "",
    management_continuity_pref: existing.management_continuity_pref || "",
    motivation: existing.motivation || "",
  });

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggle = (k, v) =>
    setForm((p) => ({ ...p, [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v] }));

  const validateStep = () => {
    if (step === 0) {
      if (!form.current_position.trim()) return "Indica tu puesto actual.";
      if (form.years_experience === "" || Number(form.years_experience) < 0) return "Indica tus años de experiencia.";
      if (!form.education) return "Selecciona tu formación.";
      if (form.background.length === 0) return "Selecciona al menos un área de background.";
    }
    if (step === 1) {
      if (!form.fund_model) return "Selecciona el modelo de tu search fund.";
      if (!form.search_stage) return "Indica en qué etapa estás.";
      if (!form.dedication) return "Indica tu dedicación.";
    }
    if (step === 2) {
      if (form.sectors_interest.length === 0) return "Selecciona al menos un sector objetivo.";
      if (form.geographies.length === 0) return "Selecciona al menos una geografía.";
    }
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) { toast.error(err); return; }
    setStep((s) => s + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/users/searcher-profile", {
        current_position: form.current_position.trim(),
        years_experience: Number(form.years_experience),
        education: form.education,
        background: form.background,
        linkedin_url: form.linkedin_url.trim() || null,
        fund_name: form.fund_name.trim() || null,
        fund_model: form.fund_model,
        search_stage: form.search_stage,
        committed_capital_eur: form.committed_capital_eur !== "" ? Number(form.committed_capital_eur) : null,
        backers_count: form.backers_count !== "" ? Number(form.backers_count) : null,
        dedication: form.dedication,
        sectors_interest: form.sectors_interest,
        geographies: form.geographies,
        ebitda_min_eur: form.ebitda_min_eur !== "" ? Number(form.ebitda_min_eur) : null,
        ebitda_max_eur: form.ebitda_max_eur !== "" ? Number(form.ebitda_max_eur) : null,
        revenue_min_eur: form.revenue_min_eur !== "" ? Number(form.revenue_min_eur) : null,
        revenue_max_eur: form.revenue_max_eur !== "" ? Number(form.revenue_max_eur) : null,
        min_recurrence: form.min_recurrence || null,
        management_continuity_pref: form.management_continuity_pref || null,
        motivation: form.motivation.trim() || null,
      });
      await refresh();
      toast.success(isEdit ? "Perfil actualizado" : "Perfil completado. Bienvenido al marketplace.");
      navigate("/searcher/marketplace");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Error guardando el perfil");
    } finally {
      setLoading(false);
    }
  };

  const StepIcon = STEPS[step].icon;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-12 lg:py-16" data-testid={`searcher-onb-step-${step + 1}`}>
        <span className="font-mono-label text-gold">{isEdit ? t("Editar perfil · Searcher") : t("Onboarding · Searcher")}</span>
        <h1 className="font-serif-display text-4xl md:text-5xl lg:text-6xl mt-3 mb-4 leading-tight">
          {step === 0 && <>{t("Cuéntanos quién eres,")}{" "}{user?.name?.split(" ")[0]}.</>}
          {step === 1 && <>{t("Tu vehículo de adquisición.")}</>}
          {step === 2 && <>{t("Dibuja tu empresa ideal.")}</>}
          {step === 3 && <>{t("La última pieza: tu historia.")}</>}
        </h1>
        <p className="text-base md:text-lg text-muted-foreground max-w-2xl">
          {step === 0 && t("Tu trayectoria es exactamente lo que los inversores evalúan antes de respaldar una operación. Cuanto más completa, más fuerte llega tu candidatura a cada match.")}
          {step === 1 && t("El modelo y la etapa de tu fund determinan qué inversores encajan contigo y qué vendedores te toman en serio. Sin esto no hay triángulo.")}
          {step === 2 && t("Estas son las mismas dimensiones que los vendedores completan sobre su empresa. Tu respuesta aquí alimenta directamente el Match IA.")}
          {step === 3 && t("Los datos cierran filtros; la historia cierra deals. La IA usará tu motivación para afinar el encaje cualitativo con vendedores e inversores.")}
        </p>

        {/* Stepper */}
        <div className="mt-10 grid grid-cols-4 gap-0 hairline border-r-0">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const state = i < step ? "done" : i === step ? "active" : "todo";
            return (
              <div
                key={s.label}
                className={`hairline-r px-3 py-3 md:px-4 ${state === "active" ? "bg-[#1E3A5F] text-bone" : state === "done" ? "bg-bone" : "bg-card"}`}
                data-testid={`searcher-onb-stepper-${i + 1}`}
              >
                <div className="flex items-center gap-2">
                  <Icon className={`w-4 h-4 ${state === "active" ? "text-gold" : "text-muted-foreground"}`} />
                  <span className="font-mono-label hidden md:inline" style={{ fontSize: "0.6rem" }}>{s.label}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Step content */}
        <div className="mt-8 hairline bg-card">
          <div className="px-7 py-5 hairline-b bg-bone flex items-center gap-3">
            <div className="w-9 h-9 hairline flex items-center justify-center bg-[#1E3A5F]">
              <StepIcon className="w-4 h-4 text-gold" />
            </div>
            <div>
              <span className="font-mono-label text-gold">{STEPS[step].kicker}</span>
              <h2 className="font-serif-display text-2xl">{STEPS[step].label}</h2>
            </div>
          </div>

          <div className="p-7">
            {step === 0 && (
              <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
                <Field label={t("Puesto actual *")}>
                  <Input value={form.current_position} onChange={(e) => set("current_position", e.target.value)}
                         placeholder={t("p. ej. Associate en fondo de PE / Director de operaciones")}
                         className="rounded-none h-11" data-testid="searcher-position-input" />
                </Field>
                <Field label={t("Años de experiencia profesional *")}>
                  <Input type="number" min="0" value={form.years_experience} onChange={(e) => set("years_experience", e.target.value)}
                         placeholder="p. ej. 8" className="rounded-none h-11" data-testid="searcher-years-input" />
                </Field>
                <ChipGroup label={t("Formación más alta *")} options={EDUCATION_LEVELS} value={form.education}
                           onSelect={(v) => set("education", v)} testPrefix="education-chip" />
                <Field label={t("LinkedIn (opcional)")}>
                  <Input value={form.linkedin_url} onChange={(e) => set("linkedin_url", e.target.value)}
                         placeholder={t("https://linkedin.com/in/…")} className="rounded-none h-11" data-testid="searcher-linkedin-input" />
                </Field>
                <MultiChipGroup className="md:col-span-2" label={t("Tu background (elige todos los que apliquen) *")}
                                options={BACKGROUNDS} values={form.background}
                                onToggle={(v) => toggle("background", v)} testPrefix="background-chip" />
              </div>
            )}

            {step === 1 && (
              <div className="space-y-8">
                <CardRadioGroup label={t("Modelo de search fund *")} options={FUND_MODELS}
                                value={form.fund_model} onSelect={(v) => set("fund_model", v)} testPrefix="fundmodel" />
                <CardRadioGroup label={t("¿En qué etapa estás? *")} options={SEARCH_STAGES}
                                value={form.search_stage} onSelect={(v) => set("search_stage", v)} testPrefix="searchstage" />
                <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
                  <Field label={t("Nombre de tu fund (opcional)")}>
                    <Input value={form.fund_name} onChange={(e) => set("fund_name", e.target.value)}
                           placeholder={t("p. ej. Meridiano Capital")} className="rounded-none h-11" data-testid="searcher-fundname-input" />
                  </Field>
                  <Field label={t("Capital comprometido (€, opcional)")}>
                    <Input type="number" min="0" value={form.committed_capital_eur} onChange={(e) => set("committed_capital_eur", e.target.value)}
                           placeholder="p. ej. 400000" className="rounded-none h-11" data-testid="searcher-capital-input" />
                  </Field>
                  <Field label={t("Nº de inversores que te respaldan (opcional)")}>
                    <Input type="number" min="0" value={form.backers_count} onChange={(e) => set("backers_count", e.target.value)}
                           placeholder="p. ej. 12" className="rounded-none h-11" data-testid="searcher-backers-input" />
                  </Field>
                  <ChipGroup label={t("Dedicación *")} value={form.dedication} onSelect={(v) => set("dedication", v)}
                             options={[{ value: "full_time", label: t("Full-time") }, { value: "part_time", label: t("Part-time") }]}
                             testPrefix="dedication-chip" />
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-8">
                <MultiChipGroup label={t("Sectores objetivo (la misma lista que ven vendedores e inversores) *")}
                                options={SECTORS} values={form.sectors_interest}
                                onToggle={(v) => toggle("sectors_interest", v)} testPrefix="sector-chip" />
                <GeographySelect
                  label={t("Geografías objetivo *")}
                  value={form.geographies}
                  onChange={(v) => set("geographies", v)}
                  testPrefix="searcher-geo"
                />
                <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
                  <RangeField label={t("Rango de EBITDA objetivo (€)")}
                              minVal={form.ebitda_min_eur} maxVal={form.ebitda_max_eur}
                              onMin={(v) => set("ebitda_min_eur", v)} onMax={(v) => set("ebitda_max_eur", v)}
                              phMin="300000" phMax="1500000" testPrefix="ebitda" />
                  <RangeField label={t("Rango de facturación objetivo (€)")}
                              minVal={form.revenue_min_eur} maxVal={form.revenue_max_eur}
                              onMin={(v) => set("revenue_min_eur", v)} onMax={(v) => set("revenue_max_eur", v)}
                              phMin="2000000" phMax="15000000" testPrefix="revenue" />
                  <ChipGroup label={t("Recurrencia mínima de ingresos")} options={RECURRENCE_LEVELS}
                             value={form.min_recurrence} onSelect={(v) => set("min_recurrence", v)} testPrefix="recurrence-chip" />
                </div>
                <CardRadioGroup label={t("Sobre el equipo directivo tras la compra")} options={CONTINUITY_PREFS}
                                value={form.management_continuity_pref}
                                onSelect={(v) => set("management_continuity_pref", v)} testPrefix="continuity" />
              </div>
            )}

            {step === 3 && (
              <div className="space-y-6">
                <Field label={t("¿Por qué quieres adquirir y operar una empresa? (opcional, pero suma mucho)")}>
                  <Textarea value={form.motivation} onChange={(e) => set("motivation", e.target.value)}
                            rows={6} className="rounded-none resize-none"
                            placeholder={t("p. ej. Tras 8 años en consultoría quiero construir sobre lo que otros han creado. Busco una empresa industrial con equipo sólido donde aportar profesionalización y crecimiento…")}
                            data-testid="searcher-motivation-textarea" />
                </Field>
                <div className="hairline p-5 bg-bone flex items-start gap-3">
                  <Sparkles className="w-4 h-4 text-gold mt-1 flex-shrink-0" />
                  <p className="text-sm text-muted-foreground">
                    {t("La IA de Relevo cruzará tu historia con la narrativa de cada empresa y los criterios de cada inversor. Una motivación auténtica mejora tu posición en los matches cualitativos.")}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Nav */}
        <div className="mt-8 hairline p-5 bg-card flex items-start gap-3" data-testid="searcher-confidentiality-note">
          <Shield className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" />
          <p className="text-sm text-muted-foreground leading-relaxed">
            {t("Al registrarte, te unes a una red privada de inversión donde la confidencialidad es la norma. Los acuerdos específicos se gestionarán individualmente para cada oportunidad.")}
          </p>
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
          <span className="font-mono-label text-muted-foreground flex items-center gap-2">
            <Lock className="w-3 h-3" />{" "}{t("Tu perfil nunca se comparte sin tu permiso")}
          </span>
          <div className="flex gap-3">
            {step > 0 && (
              <Button variant="outline" onClick={() => setStep((s) => s - 1)}
                      className="rounded-none h-12 px-6" data-testid="searcher-onb-back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />{" "}{t("Atrás")}
              </Button>
            )}
            {step < 3 ? (
              <Button onClick={next} className="rounded-none bg-[#1E3A5F] hover:bg-[#2B4C78] text-bone h-12 px-8"
                      data-testid="searcher-onb-next-btn">
                {t("Continuar")}{" "}<ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={loading}
                      className="rounded-none bg-[#1E3A5F] hover:bg-[#2B4C78] text-bone h-12 px-8 disabled:opacity-50"
                      data-testid="searcher-onb-submit-btn">
                {loading ? t("Guardando…") : isEdit ? t("Guardar cambios") : t("Completar perfil y entrar")}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <Label className="font-mono-label text-muted-foreground">{label}</Label>
    {children}
  </div>
);

/** Single-select chips. Options can be strings or {value,label}. */
const ChipGroup = ({ label, options, value, onSelect, testPrefix, className = "" }) => (
  <div className={`space-y-2 ${className}`}>
    <Label className="font-mono-label text-muted-foreground">{label}</Label>
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const v = typeof o === "string" ? o : o.value;
        const l = typeof o === "string" ? o : o.label;
        const active = value === v;
        return (
          <button key={v} type="button" onClick={() => onSelect(v)} data-testid={`${testPrefix}-${v}`}
                  className={`hairline px-3 py-1.5 text-sm transition-colors ${active ? "bg-[#1E3A5F] text-bone border-[#1E3A5F]" : "bg-card hover:bg-bone"}`}>
            {l}
          </button>
        );
      })}
    </div>
  </div>
);

const MultiChipGroup = ({ label, options, values, onToggle, testPrefix, className = "" }) => (
  <div className={`space-y-2 ${className}`}>
    <Label className="font-mono-label text-muted-foreground">{label}</Label>
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button key={o} type="button" onClick={() => onToggle(o)} data-testid={`${testPrefix}-${o}`}
                className={`hairline px-3 py-1.5 text-sm transition-colors ${values.includes(o) ? "bg-[#1E3A5F] text-bone border-[#1E3A5F]" : "bg-card hover:bg-bone"}`}>
          {o}
        </button>
      ))}
    </div>
  </div>
);

const CardRadioGroup = ({ label, options, value, onSelect, testPrefix }) => (
  <div className="space-y-2">
    <Label className="font-mono-label text-muted-foreground">{label}</Label>
    <div className="grid md:grid-cols-3 gap-0 hairline border-r-0 border-b-0">
      {options.map((o) => (
        <button key={o.value} type="button" onClick={() => onSelect(o.value)} data-testid={`${testPrefix}-${o.value}`}
                className={`text-left hairline border-l-0 border-t-0 p-4 transition-colors ${value === o.value ? "bg-[#1E3A5F] text-bone" : "bg-card hover:bg-bone"}`}>
          <div className="font-serif-display text-lg mb-1">{o.label}</div>
          <div className={`text-xs ${value === o.value ? "text-bone/70" : "text-muted-foreground"}`}>{o.desc}</div>
        </button>
      ))}
    </div>
  </div>
);

const RangeField = ({ label, minVal, maxVal, onMin, onMax, phMin, phMax, testPrefix }) => (
  <div className="space-y-2">
    <Label className="font-mono-label text-muted-foreground">{label}</Label>
    <div className="flex items-center gap-2">
      <Input type="number" min="0" value={minVal} onChange={(e) => onMin(e.target.value)} placeholder={phMin}
             className="rounded-none h-11" data-testid={`searcher-${testPrefix}-min-input`} />
      <span className="text-muted-foreground">—</span>
      <Input type="number" min="0" value={maxVal} onChange={(e) => onMax(e.target.value)} placeholder={phMax}
             className="rounded-none h-11" data-testid={`searcher-${testPrefix}-max-input`} />
    </div>
  </div>
);
